import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APIService, ModelCreatePresignedURLInput } from 'src/app/API.service';
import { PriceListDataService } from '../../services/price-list-data.service';
import { PriceListLookupService } from '../../services/price-list-lookup.service';

/**
 * Bulk import for the retail price list (cjenovnik / MPC).
 *
 * A real price list holds tens of thousands of rows, which cannot be inserted
 * inside the 30s AppSync resolver timeout that the codebook and chart-of-accounts
 * importers rely on. This component therefore drives the import itself, calling
 * bulkPriceListImport repeatedly and following the `phase` / `nextOffset` the
 * backend returns, so each individual call stays small and the user gets a
 * progress bar instead of a timeout.
 */
@Component({
  selector: 'app-price-list-bulk-import',
  templateUrl: './price-list-bulk-import.component.html',
  styleUrls: ['./price-list-bulk-import.component.css'],
})
export class PriceListBulkImportComponent implements OnInit {
  fileUrl: string = '';
  hasError: boolean = false;
  loading: boolean = false;
  uploading: boolean = false;
  csvTemplateUrl: string = `https://cf-invoices-storage74835-dev.s3.eu-central-1.amazonaws.com/csv/price-list-bulk-import-template-v1.csv`;

  /** Replace the existing price list rather than appending to it. */
  replaceExisting: boolean = true;

  // Progress state shown while the chunked import runs.
  statusText: string = '';
  totalRows: number = 0;
  processedRows: number = 0;
  insertedRows: number = 0;
  failedRows: number = 0;
  importFinished: boolean = false;

  /**
   * Safety net for the chunk loop. At the backend's 1500 rows per call this
   * allows about 750k rows, far beyond any real price list, while guaranteeing
   * the loop cannot spin forever if the backend ever stopped advancing.
   */
  private readonly MAX_CHUNK_CALLS = 500;

  constructor(
    private snackBar: MatSnackBar,
    private apiService: APIService,
    private http: HttpClient,
    private priceListDataService: PriceListDataService,
    private priceListLookupService: PriceListLookupService,
    private dialogRef: MatDialogRef<PriceListBulkImportComponent>
  ) {}

  ngOnInit(): void {}

  get progressPercent(): number {
    if (!this.totalRows) return 0;
    return Math.min(100, Math.round((this.processedRows / this.totalRows) * 100));
  }

  async getPresignedUrl(file: any) {
    this.hasError = false;
    this.fileUrl = '';
    this.uploading = true;
    this.statusText = 'Uploading file…';
    try {
      const data: ModelCreatePresignedURLInput = {
        originalFileName: file.name,
        profile: 'bulkUpload',
        action: 'upload',
      };
      const response: any = await this.apiService.CreatePresignedURL(data);
      // Unlike the other bulk importers, the upload is awaited before the URL is
      // exposed: a price list CSV is large enough that the import could
      // otherwise start reading an object that is not in S3 yet.
      await this.uploadFile(response.url, file);
      this.fileUrl = response.url.split('?')[0];
      this.statusText = '';
    } catch (err) {
      console.log(err);
      this.hasError = true;
      this.statusText = 'Upload failed. Please try again.';
    } finally {
      this.uploading = false;
    }
  }

  private async uploadFile(url: string, file: any): Promise<void> {
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');
    // toPromise() rather than firstValueFrom(): this project is on rxjs 6.6.
    await this.http.put(url, file.rawFile, { headers }).toPromise();
  }

  async addCsv() {
    this.loading = true;
    this.importFinished = false;
    this.totalRows = 0;
    this.processedRows = 0;
    this.insertedRows = 0;
    this.failedRows = 0;

    try {
      // ── Phase 1: clear the previous price list, one bounded batch per call ──
      if (this.replaceExisting) {
        let guard = 0;
        let deleted = 0;
        for (;;) {
          this.statusText = deleted
            ? `Removing the previous price list… (${deleted} removed)`
            : 'Removing the previous price list…';
          const res = await this.priceListDataService.bulkImportChunk(
            this.fileUrl,
            { replace: true }
          );
          if (res.result === 'FAILED') return this.reportFailure(res.message);
          deleted += res.deletedRows || 0;
          // The backend rolls straight into the first import chunk once the old
          // rows are gone, so anything other than CLEARING means we are done here.
          if (res.phase !== 'CLEARING') {
            this.applyProgress(res);
            if (res.done) return this.reportSuccess();
            break;
          }
          if (++guard >= this.MAX_CHUNK_CALLS) {
            return this.reportFailure(
              'Removing the previous price list is taking too long. Please try again.'
            );
          }
        }
      }

      // ── Phase 2: import the remaining rows chunk by chunk ──────────────────
      let offset = this.processedRows;
      let guard = 0;
      while (true) {
        this.statusText = this.totalRows
          ? `Importing ${this.processedRows} of ${this.totalRows} rows…`
          : 'Importing…';
        const res = await this.priceListDataService.bulkImportChunk(
          this.fileUrl,
          { offset }
        );
        if (res.result === 'FAILED') return this.reportFailure(res.message);

        this.applyProgress(res);
        if (res.done) break;

        // Guard against a backend that stops advancing, which would otherwise
        // loop forever re-importing the same slice.
        const next = res.nextOffset ?? offset;
        if (next <= offset) {
          return this.reportFailure(
            'The import stopped making progress. Please try again.'
          );
        }
        offset = next;

        if (++guard >= this.MAX_CHUNK_CALLS) {
          return this.reportFailure(
            'The file is larger than the importer supports in one run.'
          );
        }
      }

      this.reportSuccess();
    } catch (err) {
      console.log(err);
      this.reportFailure(
        'The import was interrupted. Reopen the price list to see how many rows were saved.'
      );
    } finally {
      this.loading = false;
    }
  }

  private applyProgress(res: any) {
    if (res.totalRows) this.totalRows = res.totalRows;
    if (res.processedRows !== undefined && res.processedRows !== null) {
      this.processedRows = res.processedRows;
    }
    this.insertedRows += res.insertedRows || 0;
    this.failedRows += res.failedRows || 0;
  }

  private reportSuccess() {
    this.importFinished = true;
    this.statusText = `Imported ${this.insertedRows} of ${this.totalRows} rows.`;
    // The in-memory MPC index is now stale; drop it so the next invoice preview
    // reloads the new prices.
    this.priceListLookupService.reset();
    this.snackBar.open(
      this.failedRows
        ? `Price list imported: ${this.insertedRows} rows saved, ${this.failedRows} failed.`
        : `Price list imported: ${this.insertedRows} rows saved.`,
      '',
      { duration: 6000, panelClass: ['white-snackbar'] }
    );
    this.dialogRef.close(true);
  }

  private reportFailure(message?: string) {
    this.hasError = true;
    this.statusText = message || 'Bulk import failed';
    this.priceListLookupService.reset();
    this.snackBar.open(this.statusText, '', {
      duration: 8000,
      panelClass: ['white-snackbar'],
    });
  }

  close() {
    // Report a partial import so the table still refreshes.
    this.dialogRef.close(this.processedRows > 0);
  }
}
